import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Archive, ChevronLeft, Download, Loader2, RotateCcw } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  archiveContracts,
  fetchArchiveReview,
  keepContracts,
  unarchiveContract,
  type ArchiveReviewItem,
} from "@/api/contracts";

/**
 * G3-S3 — Contracts Archive Review (visual ④, D-060 / D3=B).
 * archive_candidate list + reason badges + per-item Архівувати/Лишити + bulk
 * (excludes recent-ship-on-inactive-LE) + CSV + un-archive (archived filter).
 * SOFT/reversible; never hard-deletes. ACL-scoped (server).
 */

type Filter = "candidate" | "archived";

const KEY = ["contracts", "archiveReview"] as const;

function ReasonBadge({ r }: { r: ArchiveReviewItem["reason"] }) {
  if (r === "inactive_le_recent_ship") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-warning px-2 py-0.5 text-[11px] font-medium text-warning-foreground">
        <AlertTriangle className="h-3 w-3" />на пенсіонованій LE, але вантажить
      </span>
    );
  }
  return (
    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
      без поставок 12м
    </span>
  );
}

export function ArchiveReviewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>("candidate");
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const q = useQuery({
    queryKey: [...KEY, filter],
    queryFn: ({ signal }) => fetchArchiveReview(filter, signal),
    staleTime: 15_000,
  });
  const items = q.data?.items ?? [];

  const showToast = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    window.setTimeout(() => setToast(null), 4500);
  };
  const refresh = () => {
    qc.invalidateQueries({ queryKey: KEY });
    setSel(new Set());
  };

  const archiveM = useMutation({
    mutationFn: ({ ids, bulk }: { ids: string[]; bulk: boolean }) => archiveContracts(ids, bulk),
    onSuccess: (r) => {
      const rej = r.rejectedLive?.length ?? 0;
      showToast("ok", `Архівовано ${r.count}${rej ? ` · пропущено ${rej} живих (поштучно)` : ""}`);
      refresh();
    },
    onError: (e) => showToast("err", e instanceof Error ? e.message : "Помилка"),
  });
  const keepM = useMutation({
    mutationFn: (ids: string[]) => keepContracts(ids),
    onSuccess: (r) => { showToast("ok", `Залишено ${r.count}`); refresh(); },
    onError: (e) => showToast("err", e instanceof Error ? e.message : "Помилка"),
  });
  const unarchM = useMutation({
    mutationFn: (id: string) => unarchiveContract(id),
    onSuccess: () => { showToast("ok", "Повернуто з архіву (зафіксовано)"); refresh(); },
    onError: (e) => showToast("err", e instanceof Error ? e.message : "Помилка"),
  });

  const bulkEligibleIds = useMemo(
    () => items.filter((i) => i.bulkEligible).map((i) => i.id),
    [items],
  );
  const selBulkEligible = useMemo(
    () => [...sel].filter((id) => bulkEligibleIds.includes(id)),
    [sel, bulkEligibleIds],
  );

  const toggle = (id: string) =>
    setSel((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const toggleAll = () =>
    setSel((s) => (s.size === bulkEligibleIds.length ? new Set() : new Set(bulkEligibleIds)));

  const exportCsv = () => {
    const head = ["number", "account", "usageStatus", "lastShipmentDate", "shipmentCount12m", "turnover12m", "reason"];
    const rows = items.map((i) =>
      [i.number, i.account, i.usageStatus, i.lastShipmentDate ?? "", i.shipmentCount12m, i.turnover12m ?? "", i.reason]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","),
    );
    const blob = new Blob(["﻿" + [head.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `archive-review-${filter}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const busy = archiveM.isPending || keepM.isPending || unarchM.isPending;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <button onClick={() => navigate("/contracts")} className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" />Договори
      </button>

      <div className="mb-3 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Архів-рев'ю договорів</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            BI-відвантаження · рекомендація, не авто-зміна — підтверджуйте вручну (D3=B).
          </p>
        </div>
        <button onClick={exportCsv} disabled={!items.length} className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50">
          <Download className="h-4 w-4" />CSV
        </button>
      </div>

      <div className="mb-3 flex gap-2 text-sm">
        {(["candidate", "archived"] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => { setFilter(f); setSel(new Set()); }}
            className={cn("rounded-full px-3 py-1", filter === f ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}
          >
            {f === "candidate" ? "Кандидати" : "Архів"}
            {q.data && filter === f ? ` · ${q.data.total}` : ""}
          </button>
        ))}
      </div>

      {filter === "candidate" && q.data && (
        <div className="mb-3 rounded-md border border-warning-foreground/30 bg-warning px-3 py-2 text-xs text-warning-foreground">
          Bulk-архів охоплює лише {q.data.bulkEligible} «без поставок 12м». «На пенсіонованій LE, але вантажить» —
          живі договори, лише поштучно (виключені з масового).
        </div>
      )}

      <div className="rounded-lg border bg-card">
        {q.isLoading ? (
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />Завантаження…
          </div>
        ) : q.isError ? (
          <div className="p-10 text-center text-sm text-destructive-soft-foreground">Помилка завантаження.</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            {filter === "candidate" ? "Кандидатів на архів немає." : "Архівованих договорів немає."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                {filter === "candidate" && (
                  <th className="px-3 py-2.5">
                    <input type="checkbox" checked={sel.size > 0 && sel.size === bulkEligibleIds.length} onChange={toggleAll} aria-label="Обрати всі" />
                  </th>
                )}
                <th className="px-3 py-2.5 font-medium">Договір</th>
                <th className="px-2 py-2.5 font-medium">Клієнт</th>
                <th className="px-2 py-2.5 font-medium">Причина</th>
                <th className="px-2 py-2.5 font-medium">Остання відвант.</th>
                <th className="px-2 py-2.5 text-right font-medium">Відвант. 12м</th>
                <th className="px-2 py-2.5 text-right font-medium">Оборот 12м</th>
                <th className="px-2 py-2.5 font-medium">Дія</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t">
                  {filter === "candidate" && (
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        disabled={!i.bulkEligible}
                        title={i.bulkEligible ? "" : "Живий договір — лише поштучно"}
                        checked={sel.has(i.id)}
                        onChange={() => toggle(i.id)}
                      />
                    </td>
                  )}
                  <td className="px-3 py-2.5">
                    <button onClick={() => navigate(`/contracts/${i.id}`)} className="font-medium text-primary hover:underline">
                      {i.number || i.id}
                    </button>
                  </td>
                  <td className="px-2 py-2.5">{i.account}</td>
                  <td className="px-2 py-2.5"><ReasonBadge r={i.reason} /></td>
                  <td className="px-2 py-2.5 tabular-nums text-muted-foreground">{i.lastShipmentDate ?? "—"}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums">{i.shipmentCount12m}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums">
                    {i.turnover12m != null ? `${i.turnover12m.toLocaleString("uk-UA")} ₴` : "—"}
                  </td>
                  <td className="px-2 py-2.5">
                    {filter === "candidate" ? (
                      <div className="flex gap-1.5">
                        <button onClick={() => archiveM.mutate({ ids: [i.id], bulk: false })} disabled={busy}
                          className="rounded border px-2 py-1 text-xs hover:border-primary hover:text-primary disabled:opacity-50">Архівувати</button>
                        <button onClick={() => keepM.mutate([i.id])} disabled={busy}
                          className="rounded border px-2 py-1 text-xs hover:bg-accent disabled:opacity-50">Лишити</button>
                      </div>
                    ) : (
                      <button onClick={() => unarchM.mutate(i.id)} disabled={busy}
                        className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs hover:border-primary hover:text-primary disabled:opacity-50">
                        <RotateCcw className="h-3 w-3" />Повернути
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {filter === "candidate" && selBulkEligible.length > 0 && (
        <div className="sticky bottom-4 mt-3 flex items-center gap-2 rounded-lg border bg-card px-4 py-2.5 shadow-lg">
          <span className="text-sm">Обрано {selBulkEligible.length}</span>
          <button onClick={() => archiveM.mutate({ ids: selBulkEligible, bulk: true })} disabled={busy}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50">
            <Archive className="h-4 w-4" />Архівувати обрані
          </button>
          <button onClick={() => keepM.mutate(selBulkEligible)} disabled={busy}
            className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50">Лишити обрані</button>
        </div>
      )}

      {toast && (
        <div role="status" className={cn("fixed bottom-4 right-4 z-50 rounded-lg px-4 py-2.5 text-sm shadow-lg",
          toast.kind === "ok" ? "bg-success text-success-foreground" : "bg-destructive-soft text-destructive-soft-foreground")}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
